import GlobeEuropeAfricaIcon from '@heroicons/react/24/solid/GlobeEuropeAfricaIcon';
import { InputAdornment, SvgIcon, Tab } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { ReferidoDataComponentPreventa } from '@/erp/operaciones/preagendamiento/shared/components';
import { a11yProps, gridSizeMdLg6, parentescoMockupData } from '@/shared';
import {
  CustomAutocomplete,
  CustomNumberTextField,
  CustomTabPanel,
  CustomTextField,
  FormTabsOnly,
  NestedTabsScene,
  SingleSaveButton,
  TabTextLabel,
} from '@/shared/components/ui';
import CustomCellphoneTextField from '@/shared/components/ui/CustomCellphoneTextField';
import {
  AlertSeverityEnum,
  Cliente,
  Contrato,
  IDataPreventa,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

export type ClienteInfoSummaryFormPartProps = {
  contract: Contrato;
  valueTab: number;
  handleMethodChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
};

// PERF: se aplana la intersección gigante (Contrato & Cliente & IDataPreventa, ~257 props
// anidadas) que hacía explotar a react-hook-form (Path<T>) → tsc de este archivo de ~30 min.
// Mismo comportamiento en runtime; el form solo usa campos planos.
// PERF: interfaz FLACA (antes Contrato & Cliente & IDataPreventa, ~257 props anidadas que hacían
// explotar Path<T>). Campos como string → errors.X sigue siendo FieldError (no Merge). Runtime igual.
type ClienteSummaryFormData = {
  razon_social: string; tipo_identificacion: string; identificacion: string;
  fecha_nacimiento: string; edad: string; pais: string; provincia: string; ciudad: string;
  zona: string; sector: string; direccion: string; coordenadas: string;
  celular: string; celular_2: string; email: string;
  persona_referencia_nombre: string; persona_referencia_parentesco: string; persona_referencia_celular: string;
};

const ClienteInfoSummaryFormPart: React.FC<ClienteInfoSummaryFormPartProps> = ({
  contract,

  handleMethodChange,
  valueTab,
}) => {
  ///* global -------------------------
  const setAlert = useUiStore(s => s.setAlert);

  ///* form -------------------------
  const form = useForm<ClienteSummaryFormData>();
  const { errors } = form.formState;

  ///* handlers -------------------------
  const onSave = (data: ClienteSummaryFormData) => {
    console.log('data', data);
  };

  ///* effects -------------------------
  useEffect(() => {
    if (!contract) return;

    const { cliente_data, preventa_data, ...rest } = contract || {};

    form.reset({
      ...preventa_data,
      ...(cliente_data || {}),
      ...rest,

      pais: rest.pais_data?.nombre || cliente_data?.pais,
      provincia: rest.provincia_data?.nombre || cliente_data?.provincia,
      ciudad: rest.ciudad_data?.nombre || cliente_data?.ciudad,
      zona: rest.zona_data?.nombre || cliente_data?.zona,
      sector: rest.sector_data?.nombre || cliente_data?.sector,

      persona_referencia_nombre: preventa_data?.persona_referencia_nombre || '',

      planLabel: contract?.plan_data?.nombre || '',
      permanencia: contract?.plan_data?.permanencia || '',
    } as unknown as ClienteSummaryFormData);
  }, [contract]);

  return (
    <>
      <TabTextLabel text="Datos del Cliente" pt="12px" pl="0" />

      <NestedTabsScene
        tabs={
          <FormTabsOnly onChange={handleMethodChange} value={valueTab}>
            <Tab label="Datos personales" value={1} {...a11yProps(1)} />
            <Tab label="Datos referencia" value={2} {...a11yProps(2)} />
            <Tab label="Datos de seguimiento" value={3} {...a11yProps(3)} />
          </FormTabsOnly>
        }
      >
        {/* --------- cliente info --------- */}
        <CustomTabPanel index={valueTab} value={1}>
          <CustomTextField
            name="razon_social"
            label="Nombre"
            control={form.control}
            defaultValue={form.getValues().razon_social || ''}
            error={errors.razon_social}
            helperText={errors.razon_social?.message?.toString() ?? ''}
            shrink
            disabled
          />
          <CustomTextField
            label="Tipo de Documento"
            name="tipo_identificacion"
            control={form.control}
            defaultValue={form.getValues().tipo_identificacion || ''}
            error={errors.tipo_identificacion}
            helperText={errors.tipo_identificacion?.message?.toString() ?? ''}
            shrink
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="identificacion"
            label="No. Identificación"
            control={form.control}
            defaultValue={form.getValues().identificacion || ''}
            error={errors.identificacion}
            helperText={errors.identificacion?.message?.toString() ?? ''}
            shrink
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="fecha_nacimiento"
            label="Fecha de Nacimiento"
            control={form.control}
            defaultValue={form.getValues().fecha_nacimiento || ''}
            error={errors.fecha_nacimiento}
            helperText={errors.fecha_nacimiento?.message?.toString() ?? ''}
            shrink
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="edad"
            label="Edad"
            control={form.control}
            defaultValue={form.getValues().edad || ''}
            error={errors.edad}
            helperText={errors.edad?.message?.toString() ?? ''}
            shrink
            size={gridSizeMdLg6}
            disabled
          />

          <CustomTextField
            name="pais"
            label="País"
            control={form.control}
            defaultValue={form.getValues().pais || ''}
            error={errors.pais}
            helperText={errors.pais?.message?.toString() ?? ''}
            shrink
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="provincia"
            label="Provincia"
            control={form.control}
            defaultValue={form.getValues().provincia || ''}
            error={errors.provincia}
            helperText={errors.provincia?.message?.toString() ?? ''}
            shrink
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="ciudad"
            label="Ciudad"
            control={form.control}
            defaultValue={form.getValues().ciudad || ''}
            error={errors.ciudad}
            helperText={errors.ciudad?.message?.toString() ?? ''}
            shrink
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="zona"
            label="Zona"
            control={form.control}
            defaultValue={form.getValues().zona || ''}
            error={errors.zona}
            helperText={errors.zona?.message?.toString() ?? ''}
            shrink
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextField
            name="sector"
            label="Sector"
            control={form.control}
            defaultValue={form.getValues().sector || ''}
            error={errors.sector}
            helperText={errors.sector?.message?.toString() ?? ''}
            shrink
            disabled
          />

          <CustomTextField
            name="direccion"
            label="Dirección"
            control={form.control}
            defaultValue={form.getValues().direccion || ''}
            error={errors.direccion}
            helperText={errors.direccion?.message?.toString() ?? ''}
            shrink
          />
          <CustomNumberTextField
            label="Coordenadas"
            name="coordenadas"
            defaultValue={form.getValues().coordenadas || ''}
            control={form.control}
            error={errors.coordenadas}
            helperText={errors.coordenadas?.message}
            shrink
            startAdornment={
              <InputAdornment position="start">
                <SvgIcon fontSize="small">
                  <GlobeEuropeAfricaIcon />
                </SvgIcon>
              </InputAdornment>
            }
            min={-90}
            max={90}
            disabled
          />

          <CustomCellphoneTextField
            name="celular"
            label="Celular"
            control={form.control}
            defaultValue={form.getValues().celular || ''}
            error={errors.celular}
            helperText={errors.celular?.message}
            shrink
            size={gridSizeMdLg6}
          />
          <CustomCellphoneTextField
            name="celular_2"
            label="Celular 2"
            control={form.control}
            defaultValue={form.getValues().celular_2 || ''}
            error={errors.celular_2}
            helperText={errors.celular_2?.message}
            shrink
            size={gridSizeMdLg6}
            required={false}
          />
          <CustomTextField
            label="Correo Electrónico"
            name="email"
            control={form.control}
            defaultValue={form.getValues().email || ''}
            error={errors.email}
            helperText={errors.email?.message}
            shrink
            type="email"
          />
        </CustomTabPanel>

        {/* --------- referencia --------- */}
        <CustomTabPanel index={valueTab} value={2}>
          <CustomTextField
            name="persona_referencia_nombre"
            label="Persona de Referencia"
            control={form.control}
            defaultValue={form.getValues().persona_referencia_nombre || ''}
            error={errors.persona_referencia_nombre}
            helperText={errors.persona_referencia_nombre?.message}
            shrink
          />

          <CustomAutocomplete<any>
            size={gridSizeMdLg6}
            label="Parentesco"
            name="persona_referencia_parentesco"
            options={parentescoMockupData || []}
            valueKey="nombre" // name is the fk
            defaultValue={form.getValues().persona_referencia_parentesco || ''}
            isLoadingData={false}
            control={form.control}
            error={errors.persona_referencia_parentesco}
            helperText={errors.persona_referencia_parentesco?.message}
            disableClearable
          />

          <CustomCellphoneTextField
            name="persona_referencia_celular"
            label="Celular Persona Referencia"
            control={form.control}
            defaultValue={form.getValues().persona_referencia_celular || ''}
            error={errors.persona_referencia_celular}
            helperText={errors.persona_referencia_celular?.message}
            shrink
            size={gridSizeMdLg6}
          />
        </CustomTabPanel>

        {/* --------- trazabilidad --------- */}
        <CustomTabPanel index={valueTab} value={3}>
          {/* TODO: falta esta parte */}
          <div>FALTA POPULAR</div>

          <ReferidoDataComponentPreventa
            showTitle
            referido_cliente_data={contract?.referido_cliente_data!}
            tipoReferido={contract?.tipo_referido!}
            referido_flota_data={contract?.referido_flota_data!}
          />
        </CustomTabPanel>

        {/* --------- action buttons --------- */}
        <SingleSaveButton
          onSave={form.handleSubmit(onSave, () => {
            setAlert(true, 'Faltan campos requeridos', AlertSeverityEnum.error);
          })}
        />
      </NestedTabsScene>
    </>
  );
};

export default ClienteInfoSummaryFormPart;
